// 셀러 쿠폰 세그먼트 — 성과 API 1회 호출로 등록 수·사용 실적을 함께 판정 (기획 260915 정정: 유도 기준 = 등록 3개 미만)
import { sellerCouponApi, SELLER_COUPON_BASE } from '@/api/SellerCouponApi'
import { hasMenuAuth } from '@/utils/menuAuth'

// 유도(카드·배너) 노출 상한 — 등록 쿠폰이 이 수 이상이면 유도 종료
export const COUPON_GUIDE_MAX_COUPONS = 3

// 판정 캐시 TTL — 화면을 오가는 동안 재호출을 막되, 사용 발생 등 상태 변화가 화면 재진입 시 반영되게.
// 재검사 시점은 각 게이트 컴포넌트의 마운트(immediate watcher)다 — 한 화면에 머무는 동안의 실시간 전환은 스펙 아님
const SEGMENT_TTL_MS = 5 * 60 * 1000

const state = {
  loadedFor: null as number | null, // 판정을 로드한 sellerNo — 대리 로그인/셀러 전환 시 재조회
  loadedAt: 0,
  epoch: 0, // 무효화 세대 — 진행 중이던 응답이 무효화 이후 stale 커밋되는 것 방지
  perf: null as Coupon.Performance | null,
}

// 진행 중 요청 디듀프 — 여러 게이트 컴포넌트가 동시에 dispatch 해도 호출은 1번, 응답 경합 없음
let inflight: Promise<void> | null = null
let inflightEpoch = -1

const getters = {
  // 판정은 "현재 셀러로 로드된 경우"에만 유효
  perf: (state, _getters, _rootState, rootGetters): Coupon.Performance | null =>
    state.loadedFor === rootGetters.sellerNo ? state.perf : null,
  couponCount: (_state, getters): number | null => (getters.perf ? getters.perf.coupon_count : null),
  hasUsage: (_state, getters): boolean => !!getters.perf && getters.perf.lifetime.use_order_count > 0,
  /** 대시보드 유도 카드 — 등록 3개 미만이면서 사용 실적이 없을 때 (사용 발생 시 성과 카드가 이어받음) */
  showPromoCard: (_state, getters): boolean =>
    getters.couponCount !== null && getters.couponCount < COUPON_GUIDE_MAX_COUPONS && !getters.hasUsage,
  /** 상품리스트·행사 유도 배너 — 등록 3개 미만이면 사용 여부와 무관하게 유지 */
  showPromoBanner: (_state, getters): boolean => getters.couponCount !== null && getters.couponCount < COUPON_GUIDE_MAX_COUPONS,
  /** 쿠폰 빈 목록 온보딩 — 등록 이력이 아예 없을 때만 */
  isS0: (_state, getters): boolean => getters.couponCount === 0,
}

const mutations = {
  setPerformance(state, payload: { sellerNo: number; perf: Coupon.Performance }) {
    state.loadedFor = payload.sellerNo
    state.loadedAt = Date.now()
    state.perf = payload.perf
  },
  /** 쿠폰 등록 등으로 판정이 바뀌면 무효화 — 다음 dispatch 에서 재조회 */
  invalidateSegment(state) {
    state.loadedFor = null
    state.loadedAt = 0
    state.perf = null
    state.epoch += 1 // 진행 중 응답의 커밋을 무효화
  },
}

const actions = {
  async loadCouponSegment({ state, commit, rootGetters }) {
    const sellerNo = rootGetters.sellerNo
    if (!sellerNo) return
    // 같은 셀러라도 TTL 경과 시 재조회 — 사용 발생·쿠폰 변화가 세션 내 반영되게
    if (state.loadedFor === sellerNo && Date.now() - state.loadedAt < SEGMENT_TTL_MS) return
    // 권한 게이트를 action 내부에서 수행 — 무권한 셀러가 403 → 전역 인터셉터 로그아웃으로 튕기는 것 방지
    if (!hasMenuAuth(rootGetters.menus, '/coupon')) return
    // 같은 세대의 진행 중 요청에만 합류 — 무효화(epoch 증가) 후에는 새 요청을 시작한다
    if (inflight && inflightEpoch === state.epoch) return inflight
    const myEpoch = state.epoch
    inflightEpoch = myEpoch
    inflight = (async () => {
      try {
        const res = await sellerCouponApi.get(`${SELLER_COUPON_BASE}/performance`, undefined, { silentError: true })
        if (state.epoch === myEpoch) commit('setPerformance', { sellerNo, perf: res.data.data })
      } catch {
        // 판정 실패 시 노출하지 않음(과노출 방지) — stale 커밋 방지를 위해 같은 세대일 때만 무효화
        if (state.epoch === myEpoch) commit('invalidateSegment')
      } finally {
        if (inflightEpoch === myEpoch) inflight = null
      }
    })()
    return inflight
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
